import { Select, Space, Button, Card, DatePicker, Checkbox, Modal } from 'antd';
import { Grid, Integration, Field, Condition, ConditionItem } from '../models';
import { useEffect, useState } from 'react';
import { apiClient, getDataTypeName } from '../utils';
import { PlusOutlined, DeleteOutlined, TableOutlined } from '@ant-design/icons';
import { CellValueInput, RecordsPreview } from '.';
import moment from 'moment';

// Types moved to models: use ConditionItem (leaf) and Condition (group)

type ConditionPath = number[];

type Operator = {
  value: string;
  label: string;
  requiresValue: boolean;
};

const dataTypeOperators: Record<string, Operator[]> = {
  STRING: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: 'contains', label: 'İçerir', requiresValue: true },
    { value: 'startsWith', label: 'Başlar', requiresValue: true },
    { value: 'endsWith', label: 'Biter', requiresValue: true },
    { value: 'in', label: 'İçinde', requiresValue: true },
    { value: 'notIn', label: 'İçinde değil', requiresValue: true },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  TEXT: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: 'contains', label: 'İçerir', requiresValue: true },
    { value: 'startsWith', label: 'Başlar', requiresValue: true },
    { value: 'endsWith', label: 'Biter', requiresValue: true },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  INTEGER: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: '>', label: 'Büyüktür', requiresValue: true },
    { value: '>=', label: 'Büyük eşit', requiresValue: true },
    { value: '<', label: 'Küçüktür', requiresValue: true },
    { value: '<=', label: 'Küçük eşit', requiresValue: true },
    { value: 'in', label: 'İçinde', requiresValue: true },
    { value: 'notIn', label: 'İçinde değil', requiresValue: true },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  DOUBLE: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: '>', label: 'Büyüktür', requiresValue: true },
    { value: '>=', label: 'Büyük eşit', requiresValue: true },
    { value: '<', label: 'Küçüktür', requiresValue: true },
    { value: '<=', label: 'Küçük eşit', requiresValue: true },
    { value: 'in', label: 'İçinde', requiresValue: true },
    { value: 'notIn', label: 'İçinde değil', requiresValue: true },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  DECIMAL: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: '>', label: 'Büyüktür', requiresValue: true },
    { value: '>=', label: 'Büyük eşit', requiresValue: true },
    { value: '<', label: 'Küçüktür', requiresValue: true },
    { value: '<=', label: 'Küçük eşit', requiresValue: true },
    { value: 'in', label: 'İçinde', requiresValue: true },
    { value: 'notIn', label: 'İçinde değil', requiresValue: true },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  DATE: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: '>', label: 'Büyüktür', requiresValue: true },
    { value: '>=', label: 'Büyük eşit', requiresValue: true },
    { value: '<', label: 'Küçüktür', requiresValue: true },
    { value: '<=', label: 'Küçük eşit', requiresValue: true },
    { value: 'beforeCurrentDate', label: 'Şimdiki tarihten önce', requiresValue: false },
    { value: 'beforeEqualCurrentDate', label: 'Şimdiki tarihe eşit veya önce', requiresValue: false },
    { value: 'afterCurrentDate', label: 'Şimdiki tarihten sonra', requiresValue: false },
    { value: 'afterEqualCurrentDate', label: 'Şimdiki tarihe eşit veya sonra', requiresValue: false },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  DATETIME: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: '>', label: 'Büyüktür', requiresValue: true },
    { value: '>=', label: 'Büyük eşit', requiresValue: true },
    { value: '<', label: 'Küçüktür', requiresValue: true },
    { value: '<=', label: 'Küçük eşit', requiresValue: true },
    { value: 'beforeCurrentDate', label: 'Şimdiki tarihten önce', requiresValue: false },
    { value: 'beforeEqualCurrentDate', label: 'Şimdiki tarihe eşit veya önce', requiresValue: false },
    { value: 'afterCurrentDate', label: 'Şimdiki tarihten sonra', requiresValue: false },
    { value: 'afterEqualCurrentDate', label: 'Şimdiki tarihe eşit veya sonra', requiresValue: false },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  TIME: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: '>', label: 'Büyüktür', requiresValue: true },
    { value: '>=', label: 'Büyük eşit', requiresValue: true },
    { value: '<', label: 'Küçüktür', requiresValue: true },
    { value: '<=', label: 'Küçük eşit', requiresValue: true },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  BOOLEAN: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  PICKLIST: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: 'in', label: 'İçinde', requiresValue: true },
    { value: 'notIn', label: 'İçinde değil', requiresValue: true },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  MULTIPICKLIST: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: 'contains', label: 'İçerir', requiresValue: true },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  URL: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: 'contains', label: 'İçerir', requiresValue: true },
    { value: 'startsWith', label: 'Başlar', requiresValue: true },
    { value: 'endsWith', label: 'Biter', requiresValue: true },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  FOREIGNKEY: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: 'in', label: 'İçinde', requiresValue: true },
    { value: 'notIn', label: 'İçinde değil', requiresValue: true },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  MULTIFOREIGNKEY: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: 'contains', label: 'İçerir', requiresValue: true },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  LOOKUP: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: 'in', label: 'İçinde', requiresValue: true },
    { value: 'notIn', label: 'İçinde değil', requiresValue: true },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  DYNAMIC: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  RULEBASED: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  PARENTROW: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  AUTOINC: [
    { value: '==', label: 'Eşittir', requiresValue: true },
    { value: '!=', label: 'Eşit değildir', requiresValue: true },
    { value: '>', label: 'Büyüktür', requiresValue: true },
    { value: '>=', label: 'Büyük eşit', requiresValue: true },
    { value: '<', label: 'Küçüktür', requiresValue: true },
    { value: '<=', label: 'Küçük eşit', requiresValue: true },
    { value: 'in', label: 'İçinde', requiresValue: true },
    { value: 'notIn', label: 'İçinde değil', requiresValue: true },
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  SUBGRID: [
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
  FILE_UPLOAD: [
    { value: 'isNull', label: 'Boş', requiresValue: false },
    { value: 'isNotNull', label: 'Boş değil', requiresValue: false },
    { value: 'errors', label: 'Hatalar', requiresValue: true },
    { value: 'tasks', label: 'Görevler', requiresValue: true },
    { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  ],
};

const recordOperators = [
  { value: 'errors', label: 'Hatalar', requiresValue: true },
  { value: 'tasks', label: 'Görevler', requiresValue: true },
  { value: 'myTasks', label: 'Bana ait görevler', requiresValue: true },
  { value: 'activeStatus', label: 'Aktiflik durumu', requiresValue: true },
  { value: 'processStatus', label: 'İşlem durumu', requiresValue: true },
  { value: 'revisions', label: 'Revizyonlar', requiresValue: true },
  { value: 'search', label: 'Arama', requiresValue: true },
  { value: 'lastUpdatedAfter', label: 'Sonra güncellenenler', requiresValue: true },
  { value: 'lastUpdatedBefore', label: 'Önce güncellenenler', requiresValue: true },
];

export function ConditionsBuilder(props: {
  dataSetId?: string;
  integrationId?: string;
  pendingSelectMessage?: string;
  initialConditions?: Condition;
  forCurrentUser?: boolean;
  onChange?: (value: Condition) => void;
}) {
  const [dataSet, setDataSet] = useState<Grid>();
  const [integration, setIntegration] = useState<Integration>();
  const [dataSetLoading, setDataSetLoading] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [conditions, setConditions] = useState<Condition>({
    logic: 'AND',
    items: [{ objectType: 'dataElement', objectId: '', operator: '', value: '', includeOldRevisions: false }],
  });

  // Reset condition conditions when dataSetId or integrationId changes
  useEffect(() => {
    if (props.initialConditions) {
      setConditions(props.initialConditions);
    } else {
      setConditions({
        logic: 'AND',
        items: [{ objectType: 'dataElement', objectId: '', operator: '', value: '', includeOldRevisions: false }],
      });
    }
  }, [props.dataSetId, props.integrationId, props.initialConditions]);

  useEffect(() => {
    setDataSetLoading(true);
    if (props.dataSetId) {
      apiClient.get(`/grids/${props.dataSetId}`).then((response: any) => {
        setDataSet(response.data);
        setDataSetLoading(false);
      });
    } else if (props.integrationId) {
      apiClient.get(`/integrations/${props.integrationId}`).then((response: any) => {
        setIntegration(response.data);
      });
    }
  }, [props.dataSetId, props.integrationId]);

  useEffect(() => {
    if (integration) {
      apiClient.get(`/grids/${integration.primaryGridId}`).then((response: any) => {
        setDataSet(response.data);
        setDataSetLoading(false);
      });
    }
  }, [integration]);

  const getOperatorsForObject = (objectType: 'dataElement' | 'record', objectId: string): Operator[] => {
    if (objectType === 'record') {
      let filteredRecordOperators = recordOperators;
      if (dataSet?.isRevision) {
        filteredRecordOperators = filteredRecordOperators.filter(op => op.value !== 'revisions');
      }
      if (!props.forCurrentUser) {
        filteredRecordOperators = filteredRecordOperators.filter(op => op.value !== 'myTasks');
      }
      return filteredRecordOperators;
    }
    const field = dataSet?.fields.find(f => f.id === objectId);
    return field ? dataTypeOperators[field.dataType] || [] : [];
  };

  const updateConditionByPath = (path: ConditionPath, updater: (condition: Condition) => void) => {
    setConditions(prev => {
      const newConditions = JSON.parse(JSON.stringify(prev));
      let current: any = newConditions;
      // Path starts with a root sentinel (0). Traverse from index 1 to reach the target Condition.
      for (let i = 1; i < path.length; i++) {
        current = current.items[path[i]];
      }
      updater(current);
      props.onChange?.(newConditions);
      return newConditions;
    });
  };

  const addConditionItem = (path: ConditionPath) => {
    updateConditionByPath(path, condition => {
      condition.items.push({ objectType: 'dataElement', objectId: '', operator: '', value: '', includeOldRevisions: false });
    });
  };

  const addCondition = (path: ConditionPath) => {
    updateConditionByPath(path, condition => {
      condition.items.push({
        logic: 'AND',
        items: [{ objectType: 'dataElement', objectId: '', operator: '', value: '', includeOldRevisions: false }],
      });
    });
  };

  const removeConditionItem = (path: ConditionPath, conditionItemIndex: number) => {
    // If this is a root condition (path length is 1), just remove the condition
    if (path.length === 1) {
      updateConditionByPath(path, condition => {
        condition.items.splice(conditionItemIndex, 1);
        if (condition.items.length === 0) {
          condition.items.push({ objectType: 'dataElement', objectId: '', operator: '', value: '', includeOldRevisions: false });
        }
      });
      return;
    }

    // For non-root conditions, if this is the last condition, remove the entire condition
    const parentPath = path.slice(0, -1);
    const conditionIndex = path[path.length - 1];

    updateConditionByPath(parentPath, parentCondition => {
      const condition = parentCondition.items[conditionIndex] as Condition;
      if (condition.items.length === 1) {
        // Remove the entire condition
        parentCondition.items.splice(conditionIndex, 1);
        // If parent is now empty, add a default condition
        if (parentCondition.items.length === 0) {
          parentCondition.items.push({ objectType: 'dataElement', objectId: '', operator: '', value: '', includeOldRevisions: false });
        }
      } else {
        // Just remove the condition
        condition.items.splice(conditionItemIndex, 1);
      }
    });
  };

  const updateConditionItem = (path: ConditionPath, conditionIndex: number, field: keyof ConditionItem, value: any) => {
    updateConditionByPath(path, condition => {
      const conditionItem = condition.items[conditionIndex] as ConditionItem;
      (conditionItem[field] as any) = value;
    });
  };

  const updateConditionLogic = (path: ConditionPath, logic: 'AND' | 'OR') => {
    updateConditionByPath(path, condition => {
      condition.logic = logic;
    });
  };

  const getLevelColor = (level: number) => {
    const colors = [
      '#ffffff', // level 0 (root)
      '#fafafa', // level 1
      '#f5f5f5', // level 2
      '#f0f0f0', // level 3
      '#fafafa', // level 4 (repeats)
      '#f5f5f5', // level 5
    ];
    return colors[level % colors.length];
  };

  const renderConditionItem = (conditionItem: ConditionItem, path: ConditionPath, conditionItemIndex: number) => {
    const operators = getOperatorsForObject(conditionItem.objectType, conditionItem.objectId);
    // If no operators available or current operator is not valid, use first available operator
    if (!operators.length || !operators.some(op => op.value === conditionItem.operator)) {
      if (operators.length) {
        updateConditionItem(path, conditionItemIndex, 'operator', operators[0].value);
        updateConditionItem(path, conditionItemIndex, 'value', '');
        conditionItem.operator = operators[0].value;
      }
    }
    const selectedOperator = operators.find(op => op.value === conditionItem.operator) || operators[0];

    return (
      <Card
        size="small"
        style={{
          marginBottom: '1rem',
          border: '1px solid #f0f0f0',
          backgroundColor: getLevelColor(path.length),
        }}
        bodyStyle={{ padding: '1rem' }}
      >
        <Space>
          <Select
            style={{ width: 400 }}
            placeholder="Alan seçin"
            options={[
              { label: 'Kayıt', value: 'MDM_RECORD' },
              ...(dataSet?.fields.map((field: Field) => ({
                label: `${field.displayName || field.name} (${getDataTypeName(field.dataType)})`,
                value: field.id,
              })) || []),
            ]}
            showSearch
            filterOption={(input, option) => {
              if (!option?.label || !input) {
                return false;
              }
              return option.label.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0;
            }}
            value={conditionItem.objectId}
            onChange={value => {
              const isRecordCondition = value === 'MDM_RECORD';
              updateConditionItem(path, conditionItemIndex, 'objectType', isRecordCondition ? 'record' : 'dataElement');
              updateConditionItem(path, conditionItemIndex, 'objectId', value);
              // Reset operator to first available one for the field type
              const newOperators = getOperatorsForObject(isRecordCondition ? 'record' : 'dataElement', value);
              if (newOperators.length > 0) {
                const newOperator = newOperators[0].value;
                updateConditionItem(path, conditionItemIndex, 'operator', newOperator);
                updateConditionItem(path, conditionItemIndex, 'value', '');
              }
            }}
          />
          <Select
            style={{ width: 175 }}
            placeholder="Operatör"
            options={operators.map(op => ({
              label: op.label,
              value: op.value,
            }))}
            value={conditionItem.operator}
            onChange={value => {
              updateConditionItem(path, conditionItemIndex, 'operator', value);
              updateConditionItem(path, conditionItemIndex, 'value', '');
            }}
          />
          {!selectedOperator?.requiresValue && <div style={{ width: 300, display: 'inline-block' }}></div>}
          {selectedOperator?.requiresValue && renderConditionValueInput(conditionItem, path, conditionItemIndex)}
          {dataSet?.isRevision && (
            <Checkbox
              style={{ marginLeft: '1rem' }}
              checked={conditionItem.includeOldRevisions ?? false}
              onChange={e => updateConditionItem(path, conditionItemIndex, 'includeOldRevisions', e.target.checked)}
            >
              Eski revizyonlar dahil
            </Checkbox>
          )}
          <Button type="text" icon={<DeleteOutlined />} onClick={() => removeConditionItem(path, conditionItemIndex)} />
        </Space>
      </Card>
    );
  };

  const renderConditionValueInput = (condition: ConditionItem, path: ConditionPath, conditionIndex: number) => {
    // Handle both single values and arrays
    let displayValue: any = condition.value;
    if (displayValue !== null && displayValue !== undefined) {
      if (Array.isArray(displayValue)) {
        displayValue = displayValue.length > 0 ? displayValue : undefined;
      } else if (typeof displayValue === 'string' && displayValue.trim() === '') {
        displayValue = undefined;
      }
    } else {
      displayValue = undefined;
    }

    const inputWidth = 300;

    if (condition.operator === 'errors' || condition.operator === 'tasks' || (condition.operator === 'myTasks' && props.forCurrentUser)) {
      return (
        <Select
          options={[
            { label: 'Var', value: 'true' },
            { label: 'Yok', value: 'false' },
            { label: 'Tümü (varsayılan)', value: 'all' },
          ]}
          style={{ width: inputWidth }}
          placeholder="Değer"
          value={displayValue ? String(displayValue) : undefined}
          onChange={value => updateConditionItem(path, conditionIndex, 'value', value)}
        />
      );
    }

    if (condition.operator === 'activeStatus') {
      return (
        <Select
          options={[
            { label: 'Aktif (varsayılan)', value: 'active' },
            { label: 'Inaktif', value: 'inactive' },
            { label: 'Tümü', value: 'all' },
          ]}
          style={{ width: inputWidth }}
          placeholder="Değer"
          value={displayValue ? String(displayValue) : undefined}
          onChange={value => updateConditionItem(path, conditionIndex, 'value', value)}
        />
      );
    }

    if (condition.operator === 'processStatus') {
      return (
        <Select
          options={[
            { label: 'İşleniyor', value: 'processing' },
            { label: 'Hazır', value: 'ready' },
            { label: 'Tümü (varsayılan)', value: 'all' },
          ]}
          style={{ width: inputWidth }}
          placeholder="Değer"
          value={displayValue ? String(displayValue) : undefined}
          onChange={value => updateConditionItem(path, conditionIndex, 'value', value)}
        />
      );
    }

    if (condition.operator === 'revisions') {
      return (
        <Select
          options={[
            { label: 'Son', value: 'latest' },
            { label: 'Eski', value: 'previous' },
            { label: 'Tümü (varsayılan)', value: 'all' },
          ]}
          style={{ width: inputWidth }}
          placeholder="Değer"
          value={displayValue ? String(displayValue) : undefined}
          onChange={value => updateConditionItem(path, conditionIndex, 'value', value)}
        />
      );
    }

    if (condition.operator === 'lastUpdatedAfter' || condition.operator === 'lastUpdatedBefore') {
      return (
        <DatePicker
          style={{ width: inputWidth }}
          placeholder="Değer"
          value={displayValue ? moment(String(displayValue)) : undefined}
          onChange={(_, dateString) => updateConditionItem(path, conditionIndex, 'value', dateString)}
        />
      );
    }

    // Handle "in" and "notIn" operators with multiple values
    if (condition.operator === 'in' || condition.operator === 'notIn') {
      const field = dataSet?.fields.find(f => f.id === condition.objectId);

      if (field) {
        // Create a modified field for multi-select support
        const multiField = { ...field };

        // For PICKLIST, FOREIGNKEY, and LOOKUP, use the multi-select version
        if (field.dataType === 'PICKLIST') {
          multiField.dataType = 'MULTIPICKLIST';
        } else if (field.dataType === 'FOREIGNKEY' || field.dataType === 'LOOKUP') {
          multiField.dataType = 'MULTIFOREIGNKEY';
        }

        // For other data types (STRING, INTEGER, DOUBLE, DECIMAL, AUTOINC), use tags mode
        if (['STRING', 'INTEGER', 'DOUBLE', 'DECIMAL', 'AUTOINC'].includes(field.dataType)) {
          const tagsValue = Array.isArray(displayValue)
            ? displayValue.map(v => String(v))
            : displayValue
              ? [String(displayValue)]
              : [];

          return (
            <Select
              mode="tags"
              style={{ width: inputWidth }}
              placeholder="Değer girin (her değerden sonra Enter'a basın)"
              value={tagsValue}
              onChange={value => updateConditionItem(path, conditionIndex, 'value', value)}
            />
          );
        }

        // Use modified field for rendering
        return (
          <CellValueInput
            field={multiField}
            forFilter={true}
            value={displayValue}
            placeholder="Değer seçin"
            onChange={value => updateConditionItem(path, conditionIndex, 'value', value)}
          />
        );
      }
    }

    return (
      <CellValueInput
        field={dataSet?.fields.find(f => f.id === condition.objectId)}
        forFilter={true}
        value={displayValue}
        placeholder="Değer"
        onChange={value => updateConditionItem(path, conditionIndex, 'value', value)}
      />
    );
  };

  const handlePreviewClick = () => {
    setPreviewModalVisible(true);
  };

  const handlePreviewClose = () => {
    setPreviewModalVisible(false);
  };

  const renderCondition = (condition: Condition, path: ConditionPath, level: number = 0) => (
    <Card
      size="small"
      style={{
        marginBottom: '1rem',
        border: '1px solid #d9d9d9',
        borderRadius: 4,
        backgroundColor: getLevelColor(level),
      }}
      bodyStyle={{ padding: '1rem', paddingBottom: 0 }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 8, width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Select
              style={{ width: 100 }}
              value={condition.logic}
              onChange={value => updateConditionLogic(path, value)}
              options={[
                { label: 'VE', value: 'AND' },
                { label: 'VEYA', value: 'OR' },
              ]}
            />
            <Button type="link" icon={<PlusOutlined />} onClick={() => addConditionItem(path)}>
              Koşul ekle
            </Button>
            <Button type="link" icon={<PlusOutlined />} onClick={() => addCondition(path)}>
              Grup ekle
            </Button>
          </Space>
          {level === 0 && (
            <Button icon={<TableOutlined />} onClick={handlePreviewClick}>
              Veri önizleme
            </Button>
          )}
        </Space>
        <div>
          {condition.items.map((condition, conditionIndex) =>
            'logic' in condition
              ? renderCondition(condition as Condition, [...path, conditionIndex], level + 1)
              : renderConditionItem(condition as ConditionItem, path, conditionIndex),
          )}
        </div>
      </Space>
    </Card>
  );

  if (!dataSet) {
    return <span>{props.pendingSelectMessage || 'Koşul için bir öğe seçin'}</span>;
  }

  if (dataSetLoading) {
    return <span>Yükleniyor...</span>;
  }

  return (
    <>
      {renderCondition(conditions, [0])}
      <Modal title="Veri önizleme" visible={previewModalVisible} onCancel={handlePreviewClose} footer={null} width="80%" destroyOnClose>
        <RecordsPreview dataSetId={dataSet.id} conditions={conditions} />
      </Modal>
    </>
  );
}
